'use client';

import { motion } from 'framer-motion';
import { Warning, Anchor, Calendar, Stack, Play, Sparkle } from '@phosphor-icons/react/dist/ssr';
import type { CSSProperties } from 'react';
import { auroraFor, subjectTintFor, sweepDelayFor } from '@/components/FlowHub/flowChoreography';
import { SubjectGlyph } from '@/components/FlowHub/SubjectGlyph';
import { ExpandableDescription } from '@/components/Wochenplan/ExpandableDescription';
import { TaskMaterialList } from '@/components/Wochenplan/TaskMaterialList';
import type { WochenplanTask } from '@/types';

interface Props {
  task: WochenplanTask;
  isRecommended?: boolean;
  onStart: () => void;
}

/**
 * TaskCardNeu (Fokus-Mode) — visuell konsistent mit TaskTile/HeroSpotlight:
 * Glyph-Badge oben links, Type-Pill (Anchor/Layers) oben rechts, Fach-Tint-Background,
 * rotierender Edge-Shine, kein Color-Bar-Streifen. CTA-Button "Starten" bleibt.
 */
export function TaskCardNeu({ task, isRecommended, onStart }: Props) {
  const aurora = auroraFor(task.subject);
  const tint = subjectTintFor(task.subject);

  // Recommendation im Fokus-Mode: Bi-Color-Shine (Fach-Farbe + Indigo), sonst Default-Fach-Shine.
  const shineCssVars: CSSProperties = isRecommended
    ? {
        ['--shine-tone' as string]: aurora.soft,
        ['--shine-tone-2' as string]: 'rgba(22, 163, 158, 0.75)',
        ['--shine-highlight' as string]: 'rgba(255, 255, 255, 1)',
        ['--shine-duration' as string]: '5.5s',
        ['--sweep-delay' as string]: sweepDelayFor(task.id),
      }
    : {
        ['--shine-tone' as string]: aurora.soft,
        ['--shine-highlight' as string]: 'rgba(255, 255, 255, 0.95)',
        ['--shine-duration' as string]: '7s',
        ['--sweep-delay' as string]: sweepDelayFor(task.id),
      };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2 }}
      className={`relative rounded-2xl border border-slate-300/80 dark:border-white/15 py-4 px-4 sm:px-5 tile-shine ${isRecommended ? 'tile-sweep' : ''} bg-linear-to-br ${tint.light} ${tint.dark}`}
      style={{
        ...shineCssVars,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.85), 0 2px 4px rgba(15,23,42,0.06), 0 12px 28px rgba(15,23,42,0.10)',
      }}
      aria-label={`Aufgabe: ${task.title}, Noch nicht begonnen`}
    >
      <div className="relative z-[4] flex items-start gap-3">
        {/* Glyph-Badge */}
        <span
          aria-hidden
          className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white/95 dark:bg-slate-900/85 shrink-0"
          style={{
            boxShadow: `inset 0 0 0 1.5px ${aurora.hex}33, 0 1px 3px rgba(15,23,42,0.06)`,
            color: aurora.hex,
          }}
        >
          <SubjectGlyph subject={task.subject} size={22} strokeWidth={2.2} />
        </span>

        {/* Body */}
        <div className="flex-1 min-w-0">
          {/* Top-Row: Title links, Type-Pill rechts */}
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className="text-sm font-bold text-slate-900 dark:text-slate-50 line-clamp-2 font-display leading-snug">
              {task.title}
            </p>
            <TypePillNeu type={task.type} auroraHex={aurora.hex} auroraSoft={aurora.soft} />
          </div>

          {/* Meta-Zeile: Subject + Marker */}
          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
            <span className="text-xs font-semibold" style={{ color: aurora.hex }}>
              {task.subject}
            </span>
            {task.is_altlast && (
              <>
                <span aria-hidden className="text-slate-300 dark:text-slate-600">·</span>
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-rose-500 text-white text-micro font-bold shadow-soft">
                  <Warning size={10} aria-hidden />
                  Nachholen
                </span>
              </>
            )}
            {isRecommended && (
              <>
                <span aria-hidden className="text-slate-300 dark:text-slate-600">·</span>
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-brand-500 text-white text-micro font-bold shadow-soft">
                  <Sparkle size={10} aria-hidden />
                  Als Nächstes
                </span>
              </>
            )}
            {task.sichtbar_ab && (
              <>
                <span aria-hidden className="text-slate-300 dark:text-slate-600">·</span>
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 text-micro font-bold">
                  <Calendar size={10} aria-hidden />
                  ab {task.sichtbar_ab}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Start-Button */}
        <button
          onClick={(e) => { e.stopPropagation(); onStart(); }}
          className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-brand-500 hover:bg-brand-600 text-white text-xs font-bold transition-all active:scale-95 shadow-soft hover:shadow-soft self-center"
          aria-label={`${task.title} starten`}
        >
          <Play size={12} weight="fill" />
          Starten
        </button>
      </div>

      {/* Description (optional) — unterhalb der Hauptzeile, eingerückt unter den Glyph */}
      {task.description && (
        <div className="relative z-[4] mt-3 pl-14">
          <ExpandableDescription text={task.description} maxLines={4} />
        </div>
      )}

      {/* Verknüpfte Materialien zum Download */}
      <TaskMaterialList materials={task.materials ?? []} className="relative z-[4] pl-14" />
    </motion.div>
  );
}

// =============================================================================
// Type-Pill (Grundlage solid Fach-Farbe / Vertiefung outline) — gleiche
// Sprache wie TaskTile + HeroSpotlight. Beide Pills gleich prominent.
// =============================================================================

interface TypePillProps {
  type: 'grundlage' | 'vertiefung';
  auroraHex: string;
  auroraSoft: string;
}

function TypePillNeu({ type, auroraHex, auroraSoft }: TypePillProps) {
  const isGrundlage = type === 'grundlage';
  const Icon = isGrundlage ? Anchor : Stack;
  const label = isGrundlage ? 'Grundlage' : 'Vertiefung';

  if (isGrundlage) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-micro font-bold text-white shadow-soft shrink-0"
        style={{ backgroundColor: auroraHex }}
      >
        <Icon size={11} aria-hidden />
        {label}
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-micro font-bold shrink-0"
      style={{
        backgroundColor: auroraSoft,
        color: auroraHex,
        boxShadow: `inset 0 0 0 1.5px ${auroraHex}`,
      }}
    >
      <Icon size={11} aria-hidden />
      {label}
    </span>
  );
}
